import React from "react";
import Image from "next/image";

import { Typography } from "./Typography";
import Link from "next/link";

export type SectionHeadingProps = {
  iconBgColor?: string;
  title: string;
  showLogo?: boolean;
  icon?: "light" | "dark";
  titleColor?: string;
  link?: string;
  className?: string;
  customIcon?: string;
  language?: AppLocale;
};

/** True when the title is Latin script (e.g. "DISTRICT NEWS") without Telugu glyphs. */
function isLatinTitle(title: string): boolean {
  const trimmed = title.trim();
  if (!trimmed) return false;
  return /[A-Za-z]/.test(trimmed) && !/[\u0C00-\u0C7F]/.test(trimmed);
}

function resolveHeadingLanguage(title: string, language?: AppLocale): AppLocale {
  if (language === "en") return "en";
  if (language === "te" && isLatinTitle(title)) return "en";
  return language ?? (isLatinTitle(title) ? "en" : "te");
}

const SectionHeader = ({
  title,
  titleColor,
  link,
  className,
  customIcon,
  language,
}: SectionHeadingProps) => {
  const headingLanguage = resolveHeadingLanguage(title, language);
  const isEnglishDisplay = headingLanguage === "en";

  const titleClassName = [
    "text-xl md:text-2xl lg:text-[1.625rem]",
    isEnglishDisplay ? "font-display font-bold tracking-wide" : "",
    className || "",
  ]
    .filter(Boolean)
    .join(" ");

  const titleNode = (
    <Typography
      variant="h4"
      language={headingLanguage}
      color={titleColor}
      className={titleClassName}
    >
      {title}
    </Typography>
  );

  return (
    <div
      className="flex items-center justify-start gap-1.5 w-max"
      data-heading-lang={isEnglishDisplay ? "en" : undefined}
    >
      <div className="relative w-[35px] h-[47px] flex-shrink-0 flex items-center justify-center">
        <Image
          src={customIcon || "/icons/CarouselIcon.svg"}
          alt=""
          width={35}
          height={47}
          className="w-full h-full object-contain"
        />
      </div>
      {link ? <Link href={link}>{titleNode}</Link> : titleNode}
    </div>
  );
};

export default SectionHeader;
